import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, concatMap, switchMap, mergeMap, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import { DataMonitoringActions, CampaignActions } from './data-monitoring.actions';
import { CampaignService } from '../../service/campaign.service';
import { DataMonitoringService } from '../../service/data-monitoring.service';
import { Campaign, KpiBaseline, KpiQualitative, KpiRiskMap } from '../../../../graphql/generated';
import { param } from 'cypress/types/jquery';
import { ScenarioConfigurationService } from '../../service/scenario-configuration.service';
import { MessageService } from 'primeng/api';


@Injectable()
export class DataMonitoringEffects {

  loadAllKpis$ = createEffect(
    (service: DataMonitoringService = inject(DataMonitoringService)) => {
      return this.actions$.pipe(
        ofType(DataMonitoringActions.loadAllKpis),
        concatMap((props) => {

          return service.getRiskMapKpi(props.riskMap.legalEntityIds, props.riskMap.campaignId, LEVEL[props.riskMap.level as string]).pipe(
            concatMap((riskMap) =>
              service.getBaselineKpi(props.baseline.legalEntityIds, props.baseline.campaignId, LEVEL[props.baseline.level as string]).pipe(
                concatMap((baseline) =>
                  service.getQualitativeKpi(props.qualitative.legalEntityIds, props.qualitative.campaignId, LEVEL[props.qualitative.level as string]).pipe(
                    concatMap((qualitative) =>
                      service.getEvaluationKpi(props.evaluation.legalEntityIds, props.evaluation.campaignId, LEVEL[props.evaluation.level as string]).pipe(
                        mergeMap((evaluation) => [
                          DataMonitoringActions.loadRiskMapKpiSuccess({ dataMonitoring: riskMap as KpiRiskMap }),
                          DataMonitoringActions.loadBaselineKpiSuccess({ baseline: baseline as KpiBaseline }),
                          DataMonitoringActions.loadQualitativeKpiSuccess({ qualitative: qualitative as KpiQualitative }),
                          DataMonitoringActions.loadEvaluationKpiSuccess({ dataMonitoring: evaluation })
                        ])
                      )
                    )
                  )
                )
              )
            ),
            catchError((error) =>
              of(DataMonitoringActions.loadRiskMapKpiFailure({ error })) // oppure una generic failure se vuoi
            )
          );
        })
      );
    },
    { functional: true }
  );

  loadRiskMapKpi$ = createEffect(
    (service: DataMonitoringService = inject(DataMonitoringService)) => {
      return this.actions$.pipe(
        ofType(DataMonitoringActions.loadRiskMapKpi),
        switchMap((props) =>
          service.getRiskMapKpi(props.riskMap.legalEntityIds, props.riskMap.campaignId, LEVEL[props.riskMap.level as string]).pipe(
            map((response) => {
              return DataMonitoringActions.loadRiskMapKpiSuccess({
                dataMonitoring: response as KpiRiskMap
              });
            }),
            catchError((error) => of(DataMonitoringActions.loadRiskMapKpiFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  loadEvaluationKpi$ = createEffect(
    (service: DataMonitoringService = inject(DataMonitoringService)) => {
      return this.actions$.pipe(
        ofType(DataMonitoringActions.loadEvaluationKpi),
        switchMap((props) =>
          service.getEvaluationKpi(props.evaluation.legalEntityIds, props.evaluation.campaignId, LEVEL[props.evaluation.level as string]).pipe(
            map((response) => {
              return DataMonitoringActions.loadEvaluationKpiSuccess({
                dataMonitoring: response
              });
            }),
            catchError((error) => of(DataMonitoringActions.loadEvaluationKpiFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  loadBaselineKpi$ = createEffect(
    (service: DataMonitoringService = inject(DataMonitoringService)) => {
      return this.actions$.pipe(
        ofType(DataMonitoringActions.loadBaselineKpi),
        switchMap((props) =>
          service.getBaselineKpi(props.baseline.legalEntityIds, props.baseline.campaignId, LEVEL[props.baseline.level as string]).pipe(
            map((response) => {
              return DataMonitoringActions.loadBaselineKpiSuccess({
                baseline: response as KpiBaseline
              });
            }),
            catchError((error) => of(DataMonitoringActions.loadBaselineKpiFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  loadQualitativeKpi$ = createEffect(
    (service: DataMonitoringService = inject(DataMonitoringService)) => {
      return this.actions$.pipe(
        ofType(DataMonitoringActions.loadQualitativeKpi),
        switchMap((props) =>
          service.getQualitativeKpi(props.qualitative.legalEntityIds, props.qualitative.campaignId, LEVEL[props.qualitative.level as string]).pipe(
            map((response) => {
              return DataMonitoringActions.loadQualitativeKpiSuccess({
                qualitative: response as KpiQualitative
              });
            }),
            catchError((error) => of(DataMonitoringActions.loadQualitativeKpiFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );



  getLastCampaign$ = createEffect(
    (service: CampaignService = inject(CampaignService)) => {
      return this.actions$.pipe(
        ofType(
          CampaignActions.loadLastCampaign
        ),
        switchMap((parameters) =>
          service.getLastCampaign(parameters.parameters || undefined).pipe(
            concatMap((response) => {
              return [
                CampaignActions.loadLastCampaignSuccess({ campaign: response, fields: parameters.fields })
              ];
            }),
            catchError((error) =>
              of(CampaignActions.loadLastCampaignFailure({ error }))
            )
          )
        )
      );
    },
    { functional: true }
  );


  loadCampaigns$ = createEffect(
    (service: CampaignService = inject(CampaignService)) => {
      return this.actions$.pipe(
        ofType(
          CampaignActions.loadCampaigns,
          CampaignActions.loadLastCampaign
        ),
        concatMap(() =>
          service.getCampaigns().pipe(
            map((campaigns) => {
              return CampaignActions.loadCampaignsSuccess({ campaign: campaigns as Campaign[] });
            }),
            catchError((error) => of(CampaignActions.loadCampaignsFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  deleteCampaignLegalEntity$ = createEffect(
    (service: CampaignService = inject(CampaignService)) => {
      return this.actions$.pipe(
        ofType(CampaignActions.deleteCampaignLegalEntity),
        concatMap(ids =>
          service.deleteCampaignAndLegalEntitiesAssociations(ids.ids).pipe(
            map((res) => {
              return CampaignActions.deleteCampaignLegalEntitySuccess({ id: ids.campaignId, rowsAffected: res?.rowsAffected });
            }),
            catchError((error) => of(CampaignActions.deleteCampaignLegalEntityFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  // createCampaignLegalEntity$ = createEffect(
  //   (service: CampaignService = inject(CampaignService)) => {
  //     return this.actions$.pipe(
  //       ofType(CampaignActions.createCampaignLegalEntity),
  //       concatMap(ids =>
  //         service.createCampaignAndLegalEntitiesAssociations(ids.ids).pipe(
  //           map((res) => {
  //             // //console.logcampaigns)
  //             return CampaignActions.createCampaignLegalEntitySuccess({ id: ids.campaignId, rowsAffected: res?.rowsAffected });
  //           }),
  //           catchError((error) => of(CampaignActions.createCampaignLegalEntityFailure({ error })))
  //         )
  //       )
  //     );
  //   },
  //   { functional: true }
  // );

  createCampaignLegalEntity$ = createEffect(
    (
      service: CampaignService = inject(CampaignService),
      messageService: MessageService = inject(MessageService)
    ) => {
      return this.actions$.pipe(
        ofType(CampaignActions.createCampaignLegalEntity),
        concatMap((ids) =>
          service.createNewCampaign().pipe(
            map((res) => {
              messageService.add({
                summary: 'Success',
                detail: 'Campaign created',
                severity: 'success',
                life: 10000
              });

              return CampaignActions.createCampaignLegalEntitySuccess({
                id: ids.campaignId,
                rowsAffected: {}
              });
            }),

            catchError((error) => {
              messageService.add({
                summary: 'Error',
                detail: 'Failed to create campaign',
                severity: 'error',
                life: 10000
              });

              return of(
                CampaignActions.createCampaignLegalEntityFailure({ error })
              );
            })
          )
        )
      );
    },
    { functional: true }
  );

  getLastCampaignID$ = createEffect(
    (service: CampaignService = inject(CampaignService)) => {
      return this.actions$.pipe(
        ofType(
          CampaignActions.loadCampaignByID
        ),
        mergeMap(props =>
          service.getCampaignById(props?.id?.toString(), props.parameters).pipe(
            map((res) => {
              // console.log(campaigns)
              return CampaignActions.loadCampaignByIDSuccess({ campaign: res, fields: props.fields });
            }),
            catchError((error) => of(CampaignActions.loadCampaignByIDFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  getLastCampaignIDAll$ = createEffect(
    (service: CampaignService = inject(CampaignService)) => {
      return this.actions$.pipe(
        ofType(
          CampaignActions.loadCampaignByIDAll
        ),
        mergeMap(props =>
          service.getCampaignByIdWithoutKpi(props?.id?.toString()).pipe(
            map((res) => {
              return CampaignActions.loadCampaignByIDAllSuccess({ campaign: res, fields: props.fields });
            }),
            catchError((error) => of(CampaignActions.loadCampaignByIDAllFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  updateCampaignLegalEntity$ = createEffect(
    (service: CampaignService = inject(CampaignService), messageService: MessageService = inject(MessageService)) => {
      return this.actions$.pipe(
        ofType(CampaignActions.updateCampaignLegalEntity),
        concatMap(ids =>
          service.createCampaignAndLegalEntitiesAssociations(ids.ids).pipe(
            map((res) => {
              if (res?.status === true) {
                messageService.add({
                  summary: 'Success',
                  detail: res?.message ?? 'Campaign Updated',
                  severity: 'success',
                  life: 10000
                });
              } else {
                messageService.add({
                  summary: 'Error',
                  detail: res?.message ?? 'error',
                  severity: 'error',
                  life: 10000
                });
              }
              return CampaignActions.updateCampaignLegalEntitySuccess({ id: ids.campaignId, rowsAffected: res?.rowsAffected });
            }),
            catchError((error) => of(CampaignActions.updateCampaignLegalEntityFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  deleteCampaign$ = createEffect(
    (service: CampaignService = inject(CampaignService)) => {
      return this.actions$.pipe(
        ofType(
          CampaignActions.deleteCampaignLegalEntitySuccess
        ),
        mergeMap(props =>
          service.getCampaignById(props?.id?.toString(), props.parameters).pipe(
            map((res) => {
              // console.log(campaigns)
              return CampaignActions.loadCampaignByIDSuccess({ campaign: res });
            }),
            catchError((error) => of(CampaignActions.loadCampaignByIDFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  createCampaign$ = createEffect(
    (service: CampaignService = inject(CampaignService)) => {
      return this.actions$.pipe(
        ofType(
          CampaignActions.createCampaignLegalEntitySuccess
        ),
        mergeMap(props =>
          service.getCampaignById(props?.id?.toString(), props.parameters).pipe(
            map((res) => {
              // //console.logcampaigns)
              return CampaignActions.loadCampaignByIDSuccess({ campaign: res });
            }),
            catchError((error) => of(CampaignActions.loadCampaignByIDFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  // getLastCampaignIdNew$ = createEffect(
  //   (rs: CampaignService = inject(CampaignService)) => {
  //     return this.actions$.pipe(
  //       ofType(CampaignActions.loadCampaignByID,),
  //       switchMap((parameters) =>
  //         rs.getCampaignById(parameters.id.toString(), parameters.parameters).pipe(
  //           map((res) => {
  //             return CampaignActions.loadCampaignByIDSuccess({ campaign: res });
  //           }),
  //           catchError((error) => of(CampaignActions.loadCampaignByIDFailure({ error })))
  //         )
  //       )
  //     );
  //   },
  //   { functional: true }
  // );

  createSnapshotCampaign$ = createEffect(
    (service: CampaignService = inject(CampaignService)) => {
      return this.actions$.pipe(
        ofType(CampaignActions.createSnapshotCampaign),
        concatMap(({ campaignId }) =>
          service.createSnapshot(campaignId).pipe(
            map((result) => CampaignActions.createSnapshotCampaignSuccess({ result })),
            catchError((error) => of(CampaignActions.createSnapshotCampaignFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );



  closeCampaign$ = createEffect(
    (
      service: CampaignService = inject(CampaignService),
      messageService: MessageService = inject(MessageService)
    ) => {
      return this.actions$.pipe(
        ofType(CampaignActions.closeCampaign),
        mergeMap(() =>
          service.closeCampaign().pipe(
            map((res) => {
              messageService.add({
                summary: 'Warning',
                detail: 'Campaign successfully closed',
                severity: 'success',
                life: 10000
              });

              return CampaignActions.closeCampaignSuccess({
                message: res!.success
              });
            }),

            catchError((error) => {
              messageService.add({
                summary: 'Error',
                detail: 'Operation failed',
                severity: 'error',
                life: 10000
              });

              return of(
                CampaignActions.closeCampaignFailure({
                  error
                })
              );
            })
          )
        )
      );
    },
    { functional: true }
  );

  validateCampaignsByLegalEntityIds$ = createEffect(
    (service: CampaignService = inject(CampaignService)) => {
      return this.actions$.pipe(
        ofType(
          CampaignActions.validateCampaign
        ),
        concatMap(({ campaignId, legalEntityIds, rowIndex }) =>
          service.validateCampaign(campaignId, legalEntityIds).pipe(
            map((result) => {
              // //console.logcampaigns)
              return CampaignActions.validateCampaignSuccess({ validation: result, rowIndex });
            }),
            catchError((error) => of(CampaignActions.validateCampaignFailure({ error, rowIndex })))
          )
        )
      );
    },
    { functional: true }
  );

  approveCampaignsByLegalEntityIds$ = createEffect(
    (service: CampaignService = inject(CampaignService)) => {
      return this.actions$.pipe(
        ofType(
          CampaignActions.approveCampaign
        ),
        concatMap(({ campaignId, legalEntityIds, rowIndex }) =>
          service.approveCampaign(campaignId, legalEntityIds).pipe(
            map((result) => {
              return CampaignActions.approveCampaignSuccess({ validation: result, rowIndex });
            }),
            catchError((error) => of(CampaignActions.approveCampaignFailure({ error, rowIndex })))
          )
        )
      );
    },
    { functional: true }
  );
  notApproveCampaignsByLegalEntityIds$ = createEffect(
    (service: CampaignService = inject(CampaignService)) => {
      return this.actions$.pipe(
        ofType(
          CampaignActions.notApproveCampaign
        ),
        concatMap(({ campaignId, legalEntityIds, rowIndex }) =>
          service.notApproveCampaign(campaignId, legalEntityIds).pipe(
            map((result) => {
              return CampaignActions.notApproveCampaignSuccess({ validation: result, rowIndex });
            }),
            catchError((error) => of(CampaignActions.notApproveCampaignFailure({ error, rowIndex })))
          )
        )
      );
    },
    { functional: true }
  );

  submitCampaignsByLegalEntityIds$ = createEffect(
    (service: CampaignService = inject(CampaignService)) => {
      return this.actions$.pipe(
        ofType(
          CampaignActions.submittedToApproveCampaign
        ),
        concatMap(({ campaignId, legalEntityIds, rowIndex }) =>
          service.submitCampaign(campaignId, legalEntityIds).pipe(
            map((result) => {
              return CampaignActions.submittedToApproveCampaignSuccess({ validation: result, rowIndex });
            }),
            catchError((error) => of(CampaignActions.submittedToApproveCampaignFailure({ error, rowIndex })))
          )
        )
      );
    },
    { functional: true }
  );

  loadSnapshots$ = createEffect(
    (rs: DataMonitoringService = inject(DataMonitoringService)) => {
      return this.actions$.pipe(
        ofType(CampaignActions.loadSnapshots),
        mergeMap(({ year }) =>
          rs.getSnapshots(year).pipe(
            map((snapshots) => {
              return CampaignActions.loadSnapshotsSuccess({
                snapshots
              });
            }),
            catchError((error) => of(CampaignActions.loadSnapshotsFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  constructor(private actions$: Actions) { }
}


export const LEVEL: Record<string, number> = {
  AU: 0,
  L1: 1,
  L2: 2,
  L3: 3
};